import React, { useState } from 'react';
import { Grid, Menu } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { PoweroffOutlined } from '@ant-design/icons';

import Icon, { IconNames } from '../../icons/Icon';
import { PageRoutesEnum } from '../../utils/routes';

import { logout } from './api/auth';
import { hasAdminRole } from './adminAccess';
import {
  BurgerIcon,
  CloseIcon,
  LogOutButtonContainer,
  MenuContentHeader,
  MenuDivider,
  MenuItem,
  MenuLinkLabel,
  Navbar,
  NavButton,
  SiderContainer,
  SiderLogo,
  StyledDrawer,
} from './Navigation.styled';

const MenuIcon = styled(Icon)`
  width: 30px;
  height: 26px;
`;

export const Navigation: React.FC = () => {
  const screens = Grid.useBreakpoint();

  const { pathname } = useLocation();
  const selectedName = pathname.startsWith(PageRoutesEnum.matchingAdmin)
    ? PageRoutesEnum.matchingAdmin
    : `/${pathname.split('/')[1] || 'candidates'}`;
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);

  const getMenuItemStyle = (item: PageRoutesEnum) => {
    return {
      height: '60px',
      backgroundImage: selectedName === item ? 'linear-gradient(45deg, #0130f8, #5eecfc)' : '',
    };
  };

  const getMenuIcon = (iconSolid: IconNames, iconGradient: IconNames, itemKey: PageRoutesEnum) => {
    return selectedName === itemKey ? (
      <MenuIcon name={iconSolid} style={{ color: 'white' }} />
    ) : (
      <MenuIcon name={iconGradient} />
    );
  };

  const menuContent = (
    <>
      <div>
        <MenuContentHeader>
          <SiderLogo>
            <Icon name="Logo" style={{ height: '50px', width: '52px' }} />
          </SiderLogo>

          {screens.xs && (
            <NavButton onClick={() => setIsDrawerVisible(false)}>
              <CloseIcon />
            </NavButton>
          )}
        </MenuContentHeader>

        <MenuDivider />

        <Menu
          mode="inline"
          selectedKeys={[selectedName]}
          style={{ border: 'none' }}
          onSelect={() => {
            setIsDrawerVisible(false);
          }}
        >
          <MenuItem
            style={getMenuItemStyle(PageRoutesEnum.recruitments)}
            key={PageRoutesEnum.recruitments}
            icon={getMenuIcon('RecruitmentsSolid', 'RecruitmentsGradient', PageRoutesEnum.recruitments)}
          >
            <Link to={PageRoutesEnum.recruitments}>
              <MenuLinkLabel selected={selectedName === PageRoutesEnum.recruitments}>Recruitments</MenuLinkLabel>
            </Link>
          </MenuItem>
          <MenuItem
            style={getMenuItemStyle(PageRoutesEnum.clients)}
            key={PageRoutesEnum.clients}
            icon={getMenuIcon('ClientsSolid', 'ClientsGradient', PageRoutesEnum.clients)}
          >
            <Link to={PageRoutesEnum.clients}>
              <MenuLinkLabel selected={selectedName === PageRoutesEnum.clients}>Clients</MenuLinkLabel>
            </Link>
          </MenuItem>
          <MenuItem
            style={getMenuItemStyle(PageRoutesEnum.projects)}
            key={PageRoutesEnum.projects}
            icon={getMenuIcon('ProjectsSolid', 'ProjectsGradient', PageRoutesEnum.projects)}
          >
            <Link to={PageRoutesEnum.projects}>
              <MenuLinkLabel selected={selectedName === PageRoutesEnum.projects}>Projects</MenuLinkLabel>
            </Link>
          </MenuItem>
          <MenuItem
            style={getMenuItemStyle(PageRoutesEnum.candidates)}
            key={PageRoutesEnum.candidates}
            icon={getMenuIcon('CandidatesSolid', 'CandidatesGradient', PageRoutesEnum.candidates)}
          >
            <Link to={PageRoutesEnum.candidates}>
              <MenuLinkLabel selected={selectedName === PageRoutesEnum.candidates}>Candidates</MenuLinkLabel>
            </Link>
          </MenuItem>
          <MenuItem
            style={getMenuItemStyle(PageRoutesEnum.reminders)}
            key={PageRoutesEnum.reminders}
            icon={getMenuIcon('RemidnersSolid', 'RemindersGradient', PageRoutesEnum.reminders)}
          >
            <Link to={PageRoutesEnum.reminders}>
              <MenuLinkLabel selected={selectedName === PageRoutesEnum.reminders}>Reminders</MenuLinkLabel>
            </Link>
          </MenuItem>
          <MenuItem
            style={getMenuItemStyle(PageRoutesEnum.emails)}
            key={PageRoutesEnum.emails}
            icon={getMenuIcon('EmailsSolid', 'EmailsGradient', PageRoutesEnum.emails)}
          >
            <Link to={PageRoutesEnum.emails}>
              <MenuLinkLabel selected={selectedName === PageRoutesEnum.emails}>Emails</MenuLinkLabel>
            </Link>
          </MenuItem>
          <MenuItem
            style={getMenuItemStyle(PageRoutesEnum.dictionaries)}
            key={PageRoutesEnum.dictionaries}
            icon={getMenuIcon('DictionariesSolid', 'DictionariesGradient', PageRoutesEnum.dictionaries)}
          >
            <Link to={PageRoutesEnum.dictionaries}>
              <MenuLinkLabel selected={selectedName === PageRoutesEnum.dictionaries}>Dictionaries</MenuLinkLabel>
            </Link>
          </MenuItem>
          {hasAdminRole() && (
            <MenuItem
              style={getMenuItemStyle(PageRoutesEnum.matchingAdmin)}
              key={PageRoutesEnum.matchingAdmin}
              icon={getMenuIcon('MatchingSolid', 'MatchingGradient', PageRoutesEnum.matchingAdmin)}
            >
              <Link to={PageRoutesEnum.matchingAdmin} aria-label="Matching">
                <MenuLinkLabel selected={selectedName === PageRoutesEnum.matchingAdmin}>Matching</MenuLinkLabel>
              </Link>
            </MenuItem>
          )}
        </Menu>
      </div>
      <LogOutButtonContainer>
        <Menu mode="inline" style={{ border: 'none' }}>
          <MenuDivider />
          <MenuItem icon={<PoweroffOutlined style={{ width: '22px' }} />} onClick={logout}>
            <Link to={PageRoutesEnum.login}>
              <MenuLinkLabel>Log out</MenuLinkLabel>
            </Link>
          </MenuItem>
        </Menu>
      </LogOutButtonContainer>
    </>
  );

  return (
    <>
      {screens.xs ? (
        <Navbar>
          <SiderLogo>
            <Icon name="Logo" style={{ height: '50px', width: '52px' }} />
          </SiderLogo>

          {!isDrawerVisible && (
            <NavButton>
              <BurgerIcon onClick={() => setIsDrawerVisible(true)} />
            </NavButton>
          )}

          <StyledDrawer
            placement="left"
            closable={false}
            onClose={() => setIsDrawerVisible(false)}
            open={isDrawerVisible}
            width={320}
          >
            {menuContent}
          </StyledDrawer>
        </Navbar>
      ) : (
        <SiderContainer
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          {menuContent}
        </SiderContainer>
      )}
    </>
  );
};
